/**
 * UTM naming standard. Single source for the Campaign links sheet in
 * Configuration, the channel classifier in trafficAttribution.ts and our own
 * marketing links, so a value typed in one place buckets the same way in
 * every report.
 *
 * Rules: lowercase, underscores, no spaces. utm_source says where the link
 * lives, utm_medium says how it got there (fixed list below), utm_campaign is
 * `<yyyy>_<mm>_<slug>`, utm_content tells links in one placement apart,
 * utm_term is the paid search keyword and is left to the ad platform.
 *
 * Bundled into the SDK: plain loops, nothing past ES2015.
 */

export interface UtmMediumOption {
  value: string;
  label: string;
  /** Table cell: what this medium is for, a few words. */
  summary: string;
  /** Builder help text: the longer version with the gotcha. */
  hint: string;
  /** Channel the Traffic Attribution report files it under. */
  channel: string;
  /** Quick-pick sources in the link builder. */
  sources: string[];
}

export const UTM_MEDIUMS: UtmMediumOption[] = [
  {
    value: 'email',
    label: 'Email',
    summary: 'Newsletters, one-off sends',
    hint: 'Newsletters and one-off sends. Email clients send no referrer, so without this tag every email click reports as Direct.',
    channel: 'Email',
    sources: ['newsletter'],
  },
  {
    value: 'cpc',
    label: 'Paid search',
    summary: 'Google Ads, Microsoft Ads',
    hint: 'Google Ads and Microsoft Ads. Use the ad platform suffixes below rather than hand-built links.',
    channel: 'Paid Search',
    sources: ['google', 'bing'],
  },
  {
    value: 'paid_social',
    label: 'Paid social',
    summary: 'Meta, TikTok, LinkedIn ads',
    hint: 'Meta, TikTok and LinkedIn ads.',
    channel: 'Paid Social',
    sources: ['facebook', 'instagram', 'tiktok', 'linkedin'],
  },
  {
    value: 'social',
    label: 'Organic social',
    summary: 'Posts, stories, bio links',
    hint: 'Posts, stories and bio links you did not pay to promote.',
    channel: 'Organic Social',
    sources: ['facebook', 'instagram', 'tiktok', 'youtube', 'pinterest', 'nextdoor'],
  },
  {
    value: 'display',
    label: 'Display',
    summary: 'Banner buys on other sites',
    hint: 'Banner placements on other sites.',
    channel: 'Display',
    sources: [],
  },
  {
    value: 'referral',
    label: 'Referral',
    summary: 'Partner sites, directories, listings',
    hint: 'Partner sites, directories and campground listings that link to you.',
    channel: 'Referral',
    sources: [],
  },
  {
    value: 'qr',
    label: 'Print and QR',
    summary: 'Flyers, signage, business cards',
    hint: 'Flyers, signage and business cards. Reports list these under the Other channel; filter by medium to see them.',
    channel: 'Other',
    sources: ['flyer', 'signage', 'business_card'],
  },
];

export interface UtmSourceGroup {
  label: string;
  sources: string[];
}

/** The source dropdown, grouped by what the source is. */
export const UTM_SOURCE_GROUPS: UtmSourceGroup[] = [
  { label: 'Email', sources: ['newsletter'] },
  { label: 'Search engines', sources: ['google', 'bing'] },
  {
    label: 'Social',
    sources: [
      'facebook',
      'instagram',
      'tiktok',
      'linkedin',
      'youtube',
      'pinterest',
      'nextdoor',
    ],
  },
  { label: 'Print', sources: ['flyer', 'signage', 'business_card'] },
];

// Medium tests follow GA4's default channel grouping so a report here and in
// GA4 agree on what counts as paid, email, social and display.
const PAID_MEDIUM_RE = /^(.*cp.*|ppc|retargeting|paid.*)$/;
const EMAIL_MEDIUM_RE = /^(email|e-mail|e_mail|e mail)$/;
const SOCIAL_MEDIUM_RE =
  /^(social|social-network|social-media|social_network|social_media|sm)$/;
const DISPLAY_MEDIUM_RE = /^(display|banner|expandable|interstitial|cpm)$/;

const SEARCH_SOURCES = [
  'google',
  'bing',
  'microsoft',
  'yahoo',
  'duckduckgo',
  'baidu',
  'yandex',
  'ecosia',
  'brave',
  'aol',
  'ask',
];

// YouTube is deliberately absent: its ads run through Google Ads and carry a
// gclid, so paid YouTube traffic files under Paid Search with the rest.
const SOCIAL_SOURCES = [
  'facebook',
  'fb',
  'meta',
  'instagram',
  'ig',
  'tiktok',
  'linkedin',
  'twitter',
  'x',
  'threads',
  'pinterest',
  'snapchat',
  'reddit',
  'nextdoor',
  'messenger',
  'whatsapp',
];

function listHas(list: string[], value: string | undefined): boolean {
  if (!value) return false;
  for (let i = 0; i < list.length; i++) {
    if (list[i] === value) return true;
  }
  return false;
}

/**
 * Source and medium drive channel classification, so they are lowercased on
 * capture: `Email` and `email` must not become two channels.
 */
export function normalizeUtmKey(
  value: string | null | undefined,
): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim().toLowerCase();
  return trimmed || undefined;
}

/**
 * Campaign, content and term keep their case: Meta fills in the real campaign
 * name and operators expect to read it back as they typed it.
 */
export function trimUtmValue(
  value: string | null | undefined,
): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

export function isPaidMedium(medium: string | undefined): boolean {
  return Boolean(medium) && PAID_MEDIUM_RE.test(medium as string);
}

export function isEmailMedium(medium: string | undefined): boolean {
  return Boolean(medium) && EMAIL_MEDIUM_RE.test(medium as string);
}

export function isSocialMedium(medium: string | undefined): boolean {
  return Boolean(medium) && SOCIAL_MEDIUM_RE.test(medium as string);
}

export function isDisplayMedium(medium: string | undefined): boolean {
  return Boolean(medium) && DISPLAY_MEDIUM_RE.test(medium as string);
}

export function isSearchSource(source: string | undefined): boolean {
  return listHas(SEARCH_SOURCES, source);
}

export function isSocialSource(source: string | undefined): boolean {
  return listHas(SOCIAL_SOURCES, source);
}

/**
 * Enforces the naming rule as the operator types: spaces and hyphens become
 * underscores, everything outside [a-z0-9_] is dropped. A trailing
 * underscore is kept because the operator is usually mid-word.
 */
export function slugifyUtmValue(value: string): string {
  return value
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_');
}

/** `2026_10_` for October 2026: the campaign name prefix. */
export function campaignPrefix(date: Date = new Date()): string {
  const month = date.getMonth() + 1;
  return `${date.getFullYear()}_${month < 10 ? '0' : ''}${month}_`;
}

export interface UtmLinkParams {
  source: string;
  medium: string;
  campaign: string;
  content?: string;
  term?: string;
}

/**
 * Appends the UTM tags to a page URL, keeping any query and hash it already
 * has. A bare path is accepted so a relative booking link still works.
 */
export function buildUtmUrl(baseUrl: string, utm: UtmLinkParams): string {
  const base = baseUrl.trim();
  const pairs: [string, string][] = [];
  const candidates: [string, string | undefined][] = [
    ['utm_source', utm.source],
    ['utm_medium', utm.medium],
    ['utm_campaign', utm.campaign],
    ['utm_content', utm.content],
    ['utm_term', utm.term],
  ];
  for (let i = 0; i < candidates.length; i++) {
    const value = candidates[i][1];
    if (value) pairs.push([candidates[i][0], value]);
  }

  try {
    const url = new URL(base);
    for (let i = 0; i < pairs.length; i++) {
      url.searchParams.set(pairs[i][0], pairs[i][1]);
    }
    return url.toString();
  } catch {
    const hashIndex = base.indexOf('#');
    const beforeHash = hashIndex === -1 ? base : base.slice(0, hashIndex);
    const hash = hashIndex === -1 ? '' : base.slice(hashIndex);
    const query: string[] = [];
    for (let i = 0; i < pairs.length; i++) {
      query.push(`${pairs[i][0]}=${encodeURIComponent(pairs[i][1])}`);
    }
    const separator = beforeHash.indexOf('?') === -1 ? '?' : '&';
    return `${beforeHash}${separator}${query.join('&')}${hash}`;
  }
}
